import { User } from 'entities';

import { AvatarProps, Avatar, Tooltip, Row, RowProps } from 'components/Blocks';

import { OnlineBadge } from './OnlineIndication';

import { useIsUserOnline } from 'providers/Online';

import { getFirstAndLastName } from 'utils/text';

import { localFiles } from 'utils/localFiles';
import { DEFAULT_NO_OWNER_USER } from 'utils/constants';

import { cn } from 'styles';

import { resolveSizeClass } from './size';

type NameProps =
  | {
      name?: false;
      nameSide?: never;
      nameGap?: never;
      nameClassName?: never;
    }
  | {
      name?: true | string;
      nameSide?: 'left' | 'right';
      nameGap?: RowProps['gap'];
      nameClassName?: string;
    };

interface BaseAvatarProps extends Pick<AvatarProps, 'fallback'> {
  children?: never;

  className?: string;
  containerClassName?: string;

  showNameOnHover?: boolean;

  user: Pick<User, 'name' | 'id' | 'picture'>;

  size?: AvatarProps['size'] | number;
}

export type UserAvatarProps = BaseAvatarProps & NameProps;

export function UserAvatar({
  user,

  name = false,
  nameSide = 'right',
  nameGap = '2',
  nameClassName,

  showNameOnHover,
  containerClassName,

  // avatar
  size = 40,
  className,
  ...avatarProps
}: UserAvatarProps) {
  const isOnline = useIsUserOnline(user.id);

  const isNotAssigned = user.id === DEFAULT_NO_OWNER_USER.id;

  const userName = (
    <span className={nameClassName}>
      {typeof name === 'boolean' ? getFirstAndLastName(user.name) : name}
    </span>
  );

  const enabledTooltip = showNameOnHover ?? !name;

  return (
    <Row gap={nameGap} className={containerClassName}>
      {name && nameSide === 'left' && userName}

      <OnlineBadge show={isOnline}>
        <Tooltip content={user.name} disabled={!enabledTooltip} ensurePresence>
          <Avatar
            src={isNotAssigned ? localFiles.images.unknownUser : user.picture || ''}
            alt={user.name}
            name={user.name}
            className={cn(resolveSizeClass(size), className)}
            {...avatarProps}
          />
        </Tooltip>
      </OnlineBadge>

      {name && nameSide === 'right' && userName}
    </Row>
  );
}
